import { useRouter } from 'next/router'
import { useState, VFC } from 'react'
import {
  createExhibitionBookmark,
  deleteExhibitionBookmark,
} from 'src/api/exhibitionBookmark'
import { DEFAULT_IMAGE, EXHIBITION_BACKGROUND_IMAGE } from 'src/assets/images'
import { BookmarkFill, BookmarkLine } from 'src/assets/svgs'
import { Link } from 'src/elements/Link'
import { Exhibition } from 'src/models/Exhibition'
import { black } from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { exhibitionDetail, REGISTER } from 'src/utils/routes'
import styled from 'styled-components'

const registerFromExhibition = (exhibitionId: number) =>
  `${REGISTER}?utm_source=exhibition&utm_medium=referral&utm_campaign=${exhibitionId}`

export const CircuitBackground = styled.img.attrs({
  src: EXHIBITION_BACKGROUND_IMAGE,
  alt: '', // 装飾用なので alt は空で OK
})`
  position: absolute;
  top: -72px;
  left: 0;
  width: 100%;
  height: calc(100vh - 72px);
  object-fit: contain;
  object-position: top center;
  z-index: -1;
  pointer-events: none;
`

type ArticleCardProps = {
  exhibition: Exhibition
  cardWidth: number
  cardHeight: number
  imageWidth: number
  imageHeight: number
  loggedIn?: boolean
}
export const ExhibitionCard: VFC<ArticleCardProps> = ({
  exhibition,
  cardWidth,
  cardHeight,
  imageWidth,
  imageHeight,
  loggedIn = false,
}) => {
  const router = useRouter()
  const [isBookmarked, setIsBookmarked] = useState(exhibition.isBookmarked)
  const tags = exhibition.exhibitionTags
  const displayTags = tags.length > 3 ? tags.slice(0, 3) : tags

  const handleBookmarkClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!loggedIn) {
      router.push(registerFromExhibition(exhibition.id))
      return
    }

    try {
      if (isBookmarked) {
        const response = await deleteExhibitionBookmark(exhibition.id)
        if (!response.error) setIsBookmarked(false)
      } else {
        const response = await createExhibitionBookmark(exhibition.id)
        if (!response.error) setIsBookmarked(true)
      }
    } catch (error) {
      console.error('ブックマークの操作に失敗しました:', error)
    }
  }

  return (
    <CardLink href={exhibitionDetail(exhibition.id)}>
      <Card cardWidth={cardWidth} cardHeight={cardHeight}>
        <ThumbWrapper>
          <Thumb
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            src={exhibition.imageUrl || DEFAULT_IMAGE}
            alt=""
            onError={(e) => {
              ;(e.target as HTMLImageElement).src = DEFAULT_IMAGE
            }}
          />
        </ThumbWrapper>
        <BookmarkButton onClick={handleBookmarkClick}>
          {isBookmarked ? <BookmarkFill /> : <BookmarkLine />}
        </BookmarkButton>
        <Title>{exhibition.title}</Title>
        <Description>
          <Publisher>{exhibition.companyName}</Publisher>
          <TagList>
            {displayTags.map((tag) => (
              <Tag key={tag.name}>＃{tag.name}</Tag>
            ))}
            {tags.length > 3 && <Tag key="more">...</Tag>}
          </TagList>
        </Description>
      </Card>
    </CardLink>
  )
}

/* ExhibitionCard */
const CardLink = styled(Link)``
const Card = styled.article<{ cardWidth: number; cardHeight: number }>`
  background: #f8f8f8;
  box-shadow: 0px 10px 30px 0px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: ${({ cardWidth }) => cardWidth}px;
  max-height: ${({ cardHeight }) => cardHeight}px;
  width: 100%;
  height: 100%;
  padding: 24px;
  position: relative;
`
const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow: hidden;
  max-height: 52px; /* タグ2行分の高さ */
`
const Tag = styled.span`
  font-size: 11px;
  line-height: 1;
  color: ${black};
  display: inline-block;
  gap: 2px;
  align-items: center;
  padding: 6px;
  background-color: #e4e4e4;
  border-radius: 5px;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
`
const ThumbWrapper = styled.div`
  position: relative;
  width: 100%;
`

const BookmarkButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  // box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  z-index: 10;

  &:hover {
    background-color: #f5f5f5;
  }
`

const Thumb = styled.img<{ imageWidth: number; imageHeight: number }>`
  width: 100%;
  max-height: ${({ imageHeight }) => imageHeight}px;
  height: auto;
  object-fit: cover;
  display: block;
`
const Title = styled.h3`
  font-size: 18px;
  line-height: 1.4;
  font-weight: 700;
  color: rgba(51, 51, 51, 1);
  /* 2行で省略 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 51px;
`
const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const Publisher = styled.div`
  font-size: 14px;
  line-height: 1.24;
  color: rgba(128, 128, 128, 1);
  /* 1行で省略 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  text-overflow: ellipsis;
  overflow: hidden;
`
export const SideColumnExhibitionCard: VFC<ArticleCardProps> = ({
  exhibition,
  loggedIn = false,
}) => {
  const router = useRouter()
  const [isBookmarked, setIsBookmarked] = useState(exhibition.isBookmarked)
  const tags = exhibition.exhibitionTags
  const displayTags = tags.length > 3 ? tags.slice(0, 3) : tags

  const handleBookmarkClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!loggedIn) {
      router.push(registerFromExhibition(exhibition.id))
      return
    }

    try {
      if (isBookmarked) {
        const response = await deleteExhibitionBookmark(exhibition.id)
        if (!response.error) setIsBookmarked(false)
      } else {
        const response = await createExhibitionBookmark(exhibition.id)
        if (!response.error) setIsBookmarked(true)
      }
    } catch (error) {
      console.error('ブックマークの操作に失敗しました:', error)
    }
  }

  return (
    <SideColumnExhibitionCardLink href={exhibitionDetail(exhibition.id)}>
      <SideColumnExhibitionCardWrapper>
        <ThumbWrapper>
          <img
            src={exhibition.imageUrl || DEFAULT_IMAGE}
            width="100%"
            height="100%"
            alt=""
            onError={(e) => {
              ;(e.target as HTMLImageElement).src = DEFAULT_IMAGE
            }}
          />
        </ThumbWrapper>
        <BookmarkButton onClick={handleBookmarkClick}>
          {isBookmarked ? <BookmarkFill /> : <BookmarkLine />}
        </BookmarkButton>
        <SideColumnExhibitionTitle>
          {exhibition.title}
        </SideColumnExhibitionTitle>
        <SideColumnExhibitionDescription>
          <SideColumnExhibitionPublisher>
            {exhibition.companyName}
          </SideColumnExhibitionPublisher>
          {displayTags.length > 0 && (
            <SideColumnExhibitionTagList>
              {displayTags.map((tag) => (
                <SideColumnExhibitionTag key={tag.name}>
                  ＃{tag.name}
                </SideColumnExhibitionTag>
              ))}
              {tags.length > 3 && <Tag key="more">...</Tag>}
            </SideColumnExhibitionTagList>
          )}
        </SideColumnExhibitionDescription>
      </SideColumnExhibitionCardWrapper>
    </SideColumnExhibitionCardLink>
  )
}

const SideColumnExhibitionCardLink = styled(Link)``
const SideColumnExhibitionCardWrapper = styled.div`
  padding: 16px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0px 10px 30px 0px rgba(0, 0, 0, 0.16);
  position: relative;
`
const SideColumnExhibitionTitle = styled.h3`
  font-size: 16px;
  font-weight: ${fontWeightBold};
  line-height: 1.4;
  color: #333333;
  // 2行で省略（モバイル想定）
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 45px;
`
const SideColumnExhibitionDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const SideColumnExhibitionPublisher = styled.div`
  font-size: 14px;
  line-height: 1.24;
  color: #808080;
  // 1行で省略（モバイル想定）
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 20px;
`
const SideColumnExhibitionTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`
const SideColumnExhibitionTag = styled.span`
  font-size: 11px;
  line-height: 1;
  color: ${black};
  display: inline-flex;
  gap: 2px;
  align-items: center;
  padding: 8px;
  background-color: #e4e4e4;
  border-radius: 5px;
  margin-right: 4px;
  &:last-child {
    margin-right: 0;
  }
`
